import classNames from 'classnames'
import moment from 'moment'
import { useMemo } from 'react'

import { useManagementCancelOrder, useManagementDeleteOrder } from '@apps-common/apis/management/order'
import { QUERY_GET_ORDER_DETAILS, QUERY_SEARCH_ORDERS } from '@apps-common/apis/portal/orders'
import { QUERY_SEARCH_ORDERS_COUNT } from '@apps-common/apis/portal/orders/search-orders-count'
import { QUERY_SEARCH_ORDERS_INFINITE } from '@apps-common/apis/portal/orders/search-orders-infinite'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { ORDER_STATUS_CANCELLED, ORDER_STATUS_COMPLETE, ORDER_STATUS_PENDING, OrderStatus } from '@apps-common/config/orders'
import { IAppNavigationOptions } from '@apps-common/hooks/useNavigation'
import { AUTH_CLAIMS, Authorization, queryClient } from '@apps-common/lib'
import { IOrderListModel } from '@apps-common/types/portal/order'
import { ROUTE_ORDER_DETAILS } from '@apps-radionet-portal-root/src/config/private-routes'

export const managementOrderTableKeys = (navigateForward: (opts: IAppNavigationOptions) => void) => [
  {
    title: '',
    key: 'status',
    static: true,
    thClasses: 'bg-surface-2',
    tdClasses: '!pl-0 !py-0 bg-surface text-text',
    Component: (props: any) => {
      return (
        <div className='flex items-center justify-start min-h-[28px]'>
          <div
            className={classNames(
              'jost-medium text-[0.9em] capitalize rounded-r-[5px] w-[6px] h-[28px]',
              props.status === OrderStatus.pending && `bg-info text-on-info`,
              props.status === OrderStatus.complete && `bg-success text-on-success`,
              props.status === OrderStatus.cancelled && `bg-warning text-on-warning`
            )}
          />
        </div>
      )
    }
  },
  {
    title: 'Status',
    key: 'status',
    static: true,
    thClasses: 'bg-surface-2',
    tdClasses: '!pl-0 !py-0 bg-surface text-text',
    Component: (props: IOrderListModel) => {
      return (
        <span>
          {props.status === ORDER_STATUS_PENDING && (
            <span className='px-[5px] py-[2px] rounded-[5px] capitalize bg-info text-on-info'>{props.status}</span>
          )}
          {props.status === ORDER_STATUS_CANCELLED && (
            <span className='px-[5px] py-[2px] rounded-[5px] capitalize bg-warning text-on-warning'>{props.status}</span>
          )}
          {props.status === ORDER_STATUS_COMPLETE && (
            <span className='px-[5px] py-[2px] rounded-[5px] capitalize bg-success text-on-success'>{props.status}</span>
          )}
        </span>
      )
    }
  },
  {
    title: 'Order#',
    key: 'orderNo',
    static: true,
    thClasses: 'bg-surface-2 table-cell',
    tdClasses: 'bg-surface text-text table-cell'
  },
  {
    title: 'Company',
    key: 'company',
    thClasses: 'bg-surface-2 table-cell',
    tdClasses: 'bg-surface text-text table-cell'
  },
  {
    title: 'Ordered by',
    key: 'orderedBy',
    thClasses: 'bg-surface-2 hidden lg:table-cell',
    tdClasses: 'bg-surface text-text hidden lg:table-cell'
    // Component: (props: any) => (<span className='jost-light text-[0.9em]'>{props.orderedBy}</span>)
  },
  {
    title: 'Delivery',
    key: 'pickUp',
    thClasses: 'bg-surface-2 hidden lg:table-cell',
    tdClasses: 'bg-surface text-text hidden lg:table-cell',
    Component: (props: IOrderListModel) => {
      const tracking = useMemo(() => {
        if (!props.pickUp) {
          if (props.trackingUrl && props.trackingNumber) {
            return {
              hasTracking: true,
              trackingLink: `${props.trackingUrl}${props.trackingNumber}`
            }
          }

          return {
            hasTracking: false,
            trackingLink: null
          }
        }

        return null
      }, [props])

      return (
        <div className='flex items-center justify-start gap-[5px]'>
          <span>{props.pickUp ? 'Pickup' : 'Courier'}</span>
          {tracking && (
            <>
              <span> - </span>
              {!tracking.trackingLink && (
                <div className='flex items-center justify-center gap-[5px] text-[0.8rem]'>Tracking not available</div>
              )}
              {tracking.trackingLink && (
                <a
                  href={tracking.trackingLink}
                  target='_blank'
                  rel='noreferrer'
                  className='flex items-center justify-center gap-[5px] underline text-[0.8rem]'
                >
                  {props.trackingNumber}
                </a>
              )}
            </>
          )}
        </div>
      )
    }
  },
  {
    title: 'Date',
    key: 'orderDate',
    thClasses: 'bg-surface-2 hidden lg:table-cell',
    tdClasses: 'bg-surface text-text hidden lg:table-cell',
    Component: (props: any) => <>{moment(props.orderDate).format('DD/MM/YYYY')}</>
  },
  {
    title: 'Actions',
    key: 'actions',
    static: true,
    thClasses: 'text-right bg-surface-2',
    tdClasses: 'bg-surface text-text',
    Component: (props: IOrderListModel) => {
      const { mutate: cancelOrder, isPending: cancelOrderIsPending } = useManagementCancelOrder(true, () => {
        queryClient.invalidateQueries({ queryKey: QUERY_GET_ORDER_DETAILS(props.orderId) })
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS_COUNT() })
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS() })
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS_INFINITE() })
      })

      const { mutate: deleteOrder, isPending: deleteOrderIsPending } = useManagementDeleteOrder(true, () => {
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS_COUNT() })
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS() })
        queryClient.invalidateQueries({ queryKey: QUERY_SEARCH_ORDERS_INFINITE() })
      })

      return (
        <div className='flex items-center justify-end gap-[8px]'>
          <Authorization roles={AUTH_CLAIMS.managementUser.roles} scopes={AUTH_CLAIMS.managementUser.scopes}>
            {(props.status === ORDER_STATUS_PENDING || props.status === ORDER_STATUS_COMPLETE) && (
              <ModalButton
                component={ConfirmModal}
                disabled={cancelOrderIsPending}
                classes=''
                componentProps={{
                  text: 'Cancel order',
                  description: 'Confirm you would like to cancel this order?',
                  action: ConfirmModalAction.Warning
                }}
                confirmCallback={() => cancelOrder({ orderId: props.orderId })}
              >
                <ButtonRoundSm type='div' className='action-warning' tooltip={{ id: `cancel-order-${props.orderId}`, message: 'Cancel' }}>
                  <commonIcons.cancel />
                </ButtonRoundSm>
              </ModalButton>
            )}
            {(props.status === ORDER_STATUS_PENDING || props.status === ORDER_STATUS_COMPLETE) && (
              <ModalButton
                component={ConfirmModal}
                disabled={deleteOrderIsPending}
                classes=''
                componentProps={{
                  text: 'Delete order',
                  description: 'Confirm you would like to delete this order? This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteOrder({ orderId: props.orderId })}
              >
                <ButtonRoundSm type='div' className='action-error' tooltip={{ id: `delete-order-${props.orderId}`, message: 'Delete' }}>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
          </Authorization>

          <ButtonRoundSm
            type='button'
            className='action-brand-solid'
            callback={(e) => {
              e?.stopPropagation()
              navigateForward({ routeUrl: ROUTE_ORDER_DETAILS.buildPath(props.orderId), route: ROUTE_ORDER_DETAILS })
            }}
            tooltip={{ id: `order-details-${props.orderId}`, message: 'Details' }}
          >
            <commonIcons.arrowAngled />
          </ButtonRoundSm>
        </div>
      )
    }
  }
]
